import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { IndexedDBCacheService } from './indexeddb-cache.service';
import { NGXLogger } from 'ngx-logger';

export interface CacheConfig {
    cacheFirst?: boolean;
    networkFirst?: boolean;
    cacheOnly?: boolean;
    networkOnly?: boolean;
    expiryMinutes?: number;
}

@Injectable({
    providedIn: 'root'
})
export class OfflineCacheService {
    private readonly CACHE_EXPIRY_MINUTES = 60; // 1 ora di default
    private readonly REST_STORE = 'restEndpoints';
    private readonly IMAGE_STORE = 'dynamicImages';
    private readonly PAGE_STORE = 'pageData';

    constructor(
        private httpClient: HttpClient,
        private indexedDBCache: IndexedDBCacheService,
        private logger: NGXLogger
    ) { }

    async initialize(): Promise<void> {
        try {
            await this.indexedDBCache.initDB();
            this.logger.info('OfflineCacheService inizializzato con successo');
        } catch (error) {
            this.logger.error('Errore nell\'inizializzazione di OfflineCacheService:', error);
        }
    }

    /**
     * Cache first strategy - controlla prima la cache, poi la rete
     */
    cacheFirst<T>(url: string, config?: CacheConfig): Observable<T> {
        const cacheKey = this.generateCacheKey(url);

        return new Observable<T>(observer => {
            this.indexedDBCache.getItem(this.REST_STORE, cacheKey)
                .then(cachedData => {
                    if (cachedData) {
                        this.logger.info(`Dati recuperati dalla cache per: ${url}`);
                        observer.next(cachedData);
                        observer.complete();
                    } else {
                        this.subscribeToObservable(this.fetchFromNetwork<T>(url, cacheKey, config?.expiryMinutes), observer);
                    }
                })
                .catch(() => {
                    this.subscribeToObservable(this.fetchFromNetwork<T>(url, cacheKey, config?.expiryMinutes), observer);
                });
        });
    }

    /**
     * Network first strategy - prova prima la rete, poi la cache
     */
    networkFirst<T>(url: string, config?: CacheConfig): Observable<T> {
        const cacheKey = this.generateCacheKey(url);

        return this.fetchFromNetwork<T>(url, cacheKey, config?.expiryMinutes)
            .pipe(
                catchError(() => {
                    return this.createCacheFallbackObservable<T>(cacheKey, `Dati recuperati dalla cache (fallback) per: ${url}`);
                })
            );
    }

    /**
     * Cache only strategy - solo dalla cache
     */
    cacheOnly<T>(url: string): Observable<T> {
        const cacheKey = this.generateCacheKey(url);

        return new Observable<T>(observer => {
            this.indexedDBCache.getItem(this.REST_STORE, cacheKey)
                .then(cachedData => {
                    if (cachedData) {
                        observer.next(cachedData);
                        observer.complete();
                    } else {
                        observer.error(new Error('Nessun dato in cache'));
                    }
                })
                .catch(err => observer.error(err));
        });
    }

    /**
     * Network only strategy - solo dalla rete
     */
    networkOnly<T>(url: string): Observable<T> {
        return this.httpClient.get<T>(url);
    }

    /**
     * POST con cache
     */
    postWithCache<T>(url: string, body: any, config?: CacheConfig): Observable<T> {
        const cacheKey = this.generateCacheKey(url, body);

        if (config?.cacheFirst) {
            return new Observable<T>(observer => {
                this.indexedDBCache.getItem(this.REST_STORE, cacheKey)
                    .then(cachedData => {
                        if (cachedData) {
                            this.logger.info(`Risposta POST recuperata dalla cache per: ${url}`);
                            observer.next(cachedData);
                            observer.complete();
                        } else {
                            this.subscribeToObservable(this.executePost<T>(url, body, cacheKey, config?.expiryMinutes), observer);
                        }
                    })
                    .catch(() => {
                        this.subscribeToObservable(this.executePost<T>(url, body, cacheKey, config?.expiryMinutes), observer);
                    });
            });
        } else {
            return this.executePost<T>(url, body, cacheKey, config?.expiryMinutes)
                .pipe(
                    catchError(() => {
                        return this.createCacheFallbackObservable<T>(cacheKey, `Risposta POST recuperata dalla cache (fallback) per: ${url}`);
                    })
                );
        }
    }

    /**
     * Cache delle immagini dinamiche
     */
    async cacheImage(imageUrl: string): Promise<string> {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const base64 = await this.blobToBase64(blob);

            const cacheKey = this.generateCacheKey(imageUrl);
            await this.indexedDBCache.setItem(
                this.IMAGE_STORE,
                cacheKey,
                base64,
                this.CACHE_EXPIRY_MINUTES
            );

            return base64;
        } catch (error) {
            this.logger.error(`Errore nel caching dell'immagine ${imageUrl}:`, error);
            throw error;
        }
    }

    /**
     * Recupera immagine dalla cache
     */
    async getCachedImage(imageUrl: string): Promise<string | null> {
        try {
            const cacheKey = this.generateCacheKey(imageUrl);
            return await this.indexedDBCache.getItem(this.IMAGE_STORE, cacheKey);
        } catch (error) {
            this.logger.error(`Errore nel recupero dell'immagine dalla cache:`, error);
            return null;
        }
    }

    private fetchFromNetwork<T>(url: string, cacheKey: string, expiryMinutes?: number): Observable<T> {
        return this.httpClient.get<T>(url)
            .pipe(
                tap(data => this.saveToCache(cacheKey, data, expiryMinutes, `Errore nel salvare in cache ${url}:`))
            );
    }

    private executePost<T>(url: string, body: any, cacheKey: string, expiryMinutes?: number): Observable<T> {
        return this.httpClient.post<T>(url, body)
            .pipe(
                tap(data => this.saveToCache(cacheKey, data, expiryMinutes, `Errore nel salvare risposta POST in cache ${url}:`))
            );
    }

    private generateCacheKey(url: string, body?: any): string {
        const baseKey = url.replace(/[^a-zA-Z0-9]/g, '_');
        if (body) {
            const bodyHash = btoa(JSON.stringify(body)).replace(/[^a-zA-Z0-9]/g, '_');
            return `${baseKey}_${bodyHash}`;
        }
        return baseKey;
    }

    /**
     * Helper method per gestire la sottoscrizione agli Observable
     */
    private subscribeToObservable<T>(observable: Observable<T>, observer: any): void {
        observable.subscribe({
            next: data => {
                observer.next(data);
                observer.complete();
            },
            error: err => observer.error(err)
        });
    }

    /**
     * Crea un Observable per il fallback dalla cache
     */
    private createCacheFallbackObservable<T>(cacheKey: string, successMessage: string): Observable<T> {
        return new Observable<T>(observer => {
            this.indexedDBCache.getItem(this.REST_STORE, cacheKey)
                .then(cachedData => {
                    if (cachedData) {
                        this.logger.info(successMessage);
                        observer.next(cachedData);
                        observer.complete();
                    } else {
                        observer.error(new Error('Nessun dato disponibile'));
                    }
                })
                .catch(err => observer.error(err));
        });
    }

    /**
     * Helper method per salvare dati in cache con gestione errori
     */
    private saveToCache<T>(cacheKey: string, data: T, expiryMinutes?: number, errorMessage?: string): void {
        this.indexedDBCache.setItem(
            this.REST_STORE,
            cacheKey,
            data,
            expiryMinutes || this.CACHE_EXPIRY_MINUTES
        ).catch(saveErr => {
            this.logger.error(errorMessage || 'Errore nel salvare in cache:', saveErr);
        });
    }

    private blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Invalida la cache per un URL e un body specifico
     */
    async invalidate(url: string, body?: any): Promise<void> {
        const cacheKey = this.generateCacheKey(url, body);
        this.logger.info(`Invalidamento cache per: ${url}`);
        await this.indexedDBCache.removeItem(this.REST_STORE, cacheKey);
    }

    /**
     * Pulisce la cache scaduta
     */
    async cleanExpiredCache(): Promise<void> {
        await this.indexedDBCache.cleanExpiredData();
    }

    /**
     * Pulisce tutta la cache
     */
    async clearAllCache(): Promise<void> {
        await Promise.all([
            this.indexedDBCache.clearStore(this.REST_STORE),
            this.indexedDBCache.clearStore(this.IMAGE_STORE),
            this.indexedDBCache.clearStore(this.PAGE_STORE)
        ]);
    }
}